import type { Platform } from "../types/ranking";

export type PlatformStatus = "active" | "coming_soon";

export interface PlatformMetadata {
  key: Platform;
  displayName: string;
  status: PlatformStatus;
  /** Full logo (horizontal wordmark/lockup) — for logo strips and tickers. */
  logoPath: string;
  /** Compact square mark (app icon / glyph) — for small square slots. */
  iconPath: string;
  urlPattern: RegExp;
  searchUrlBuilder?: (keyword: string) => string;
}

export const PLATFORM_METADATA: Record<Platform, PlatformMetadata> = {
  GETYOURGUIDE: {
    key: "GETYOURGUIDE",
    displayName: "GetYourGuide",
    status: "active",
    logoPath: "/logos/full/getyourguide.svg",
    iconPath: "/logos/marks/getyourguide.svg",
    urlPattern: /^https?:\/\/(www\.)?getyourguide\.[a-z]+\//,
    searchUrlBuilder: (keyword) =>
      `https://www.getyourguide.com/s/?q=${encodeURIComponent(keyword)}`,
  },
  VIATOR: {
    key: "VIATOR",
    displayName: "Viator",
    status: "active",
    logoPath: "/logos/full/viator.svg",
    iconPath: "/logos/marks/viator.png",
    urlPattern: /^https?:\/\/(www\.)?viator\.com\//,
    searchUrlBuilder: (keyword) =>
      `https://www.viator.com/searchResults/all?text=${encodeURIComponent(keyword)}`,
  },
  TRIPADVISOR: {
    key: "TRIPADVISOR",
    displayName: "Tripadvisor",
    status: "coming_soon",
    logoPath: "/logos/full/tripadvisor.svg",
    iconPath: "/logos/marks/tripadvisor.png",
    urlPattern: /^https?:\/\/(www\.)?tripadvisor\.[a-z]+\//,
    searchUrlBuilder: (keyword) =>
      `https://www.tripadvisor.com/Search?q=${encodeURIComponent(keyword)}`,
  },
  FEVER: {
    key: "FEVER",
    displayName: "Fever",
    status: "coming_soon",
    logoPath: "/logos/full/fever.svg",
    iconPath: "/logos/marks/fever.png",
    urlPattern: /^https?:\/\/(www\.)?feverup\.com\//,
  },
  KLOOK: {
    key: "KLOOK",
    displayName: "Klook",
    status: "coming_soon",
    logoPath: "/logos/full/klook.svg",
    iconPath: "/logos/marks/klook.svg",
    urlPattern: /^https?:\/\/(www\.)?klook\.com\//,
  },
  TIQETS: {
    key: "TIQETS",
    displayName: "Tiqets",
    status: "coming_soon",
    logoPath: "/logos/full/tiqets.svg",
    iconPath: "/logos/marks/tiqets.png",
    urlPattern: /^https?:\/\/(www\.)?tiqets\.com\//,
  },
  HEADOUT: {
    key: "HEADOUT",
    displayName: "Headout",
    status: "coming_soon",
    logoPath: "/logos/full/headout.svg",
    iconPath: "/logos/marks/headout.png",
    urlPattern: /^https?:\/\/(www\.)?headout\.com\//,
  },
  EXPEDIA: {
    key: "EXPEDIA",
    displayName: "Expedia",
    status: "coming_soon",
    logoPath: "/logos/full/expedia.svg",
    iconPath: "/logos/marks/expedia.svg",
    urlPattern: /^https?:\/\/(www\.)?expedia\.[a-z]+\//,
  },
  AIRBNB: {
    key: "AIRBNB",
    displayName: "Airbnb Experiences",
    status: "coming_soon",
    logoPath: "/logos/full/airbnb.svg",
    iconPath: "/logos/marks/airbnb.svg",
    urlPattern: /^https?:\/\/(www\.)?airbnb\.[a-z]+\/experiences\//,
  },
  BOOKING: {
    key: "BOOKING",
    displayName: "Booking.com",
    status: "coming_soon",
    logoPath: "/logos/full/booking.svg",
    iconPath: "/logos/marks/booking.png",
    urlPattern: /^https?:\/\/(www\.)?booking\.com\//,
  },
  MUSEMENT: {
    key: "MUSEMENT",
    displayName: "Musement",
    status: "active",
    logoPath: "/logos/full/musement.svg",
    iconPath: "/logos/marks/musement.svg",
    urlPattern: /^https?:\/\/(www\.)?musement\.com\//,
    searchUrlBuilder: (keyword) =>
      `https://www.musement.com/us/search/?q=${encodeURIComponent(keyword)}`,
  },
  CIVITATIS: {
    key: "CIVITATIS",
    displayName: "Civitatis",
    status: "coming_soon",
    logoPath: "/logos/full/civitatis.svg",
    iconPath: "/logos/marks/civitatis.png",
    urlPattern: /^https?:\/\/(www\.)?civitatis\.com\//,
  },
};

// Backward-compatible derived constants
export const PLATFORMS: Platform[] = Object.keys(
  PLATFORM_METADATA,
) as Platform[];

export const PLATFORM_DISPLAY_NAMES = Object.fromEntries(
  Object.entries(PLATFORM_METADATA).map(([key, meta]) => [key, meta.displayName]),
) as Record<Platform, string>;

export const PLATFORM_URLS = Object.fromEntries(
  Object.entries(PLATFORM_METADATA).map(([key, meta]) => [key, meta.urlPattern]),
) as Record<Platform, RegExp>;

export const PLATFORM_SEARCH_URLS = Object.fromEntries(
  Object.entries(PLATFORM_METADATA)
    .filter(([, meta]) => meta.searchUrlBuilder)
    .map(([key, meta]) => [key, meta.searchUrlBuilder]),
) as Partial<Record<Platform, (keyword: string) => string>>;
